import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import useApi from '../../../hooks/useApi';
import apiService from '../../../services/apiService';
import Table from '../../../components/Table';
import Modal from '../../../components/Modal';
import FormInput from '../../../components/FormInput';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { FaTrash, FaEdit } from 'react-icons/fa';

function AboutUs() {
  const { data: abouts, loading: aboutsLoading, fetchData: fetchAbouts } = useApi('/about-us/all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAboutId, setEditingAboutId] = useState(null);
  const [aboutForm, setAboutForm] = useState({
    title: '',
    content: '',
  });
  const [aboutErrors, setAboutErrors] = useState({});

  const validateAboutForm = () => {
    const errors = {};
    if (!aboutForm.title.trim()) errors.title = 'Title is required';
    if (!aboutForm.content.trim()) errors.content = 'Content is required';
    setAboutErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAboutInputChange = (e) => {
    const { name, value } = e.target;
    setAboutForm({ ...aboutForm, [name]: value });
    setAboutErrors({ ...aboutErrors, [name]: '' });
  };

  const handleEditorChange = (event, editor) => {
    const data = editor.getData();
    setAboutForm({ ...aboutForm, content: data });
    setAboutErrors({ ...aboutErrors, content: '' });
  };

  const handleAboutSubmit = async (e) => {
    e.preventDefault();
    if (!validateAboutForm()) {
      toast.error('Please fix form errors');
      return;
    }
    try {
      const formData = {
        title: aboutForm.title,
        content: aboutForm.content,
      };

      if (editingAboutId) {
        await apiService.request('put', `/about-us/${editingAboutId}`, formData);
        toast.success('About us updated successfully');
      } else {
        await apiService.post('/about-us', formData);
        toast.success('About us added successfully');
      }
      fetchAbouts();
      setIsModalOpen(false);
      setAboutForm({
        title: '',
        content: '',
      });
      setEditingAboutId(null);
      setAboutErrors({});
    } catch (err) {
      // Error handled by apiService interceptor
    }
  };

  const handleEditAbout = (item) => {
    setAboutForm({
      title: item.title || '',
      content: item.content || '',
    });
    setEditingAboutId(item._id);
    setIsModalOpen(true);
    setAboutErrors({});
  };

  const handleDeleteAbout = async (aboutId) => {
    if (!window.confirm('Are you sure you want to delete this about us?')) return;
    try {
      await apiService.delete(`/about-us/${aboutId}`);
      toast.success('About us deleted successfully');
      fetchAbouts();
    } catch (err) {
      // Error handled by apiService interceptor
    }
  };

  const aboutColumns = [
    { key: 'title', label: 'Title', render: (item) => item.title || 'N/A' },
    { key: 'updatedAt', label: 'Last Updated', render: (item) => new Date(item.updatedAt).toLocaleString() || 'N/A' },
  ];

  const aboutActions = [
    {
      label: 'Edit',
      className: 'text-yellow-500 hover:text-yellow-600',
      icon: <FaEdit />,
      onClick: handleEditAbout,
    },
    {
      label: 'Delete',
      className: 'text-red-500 hover:text-red-600',
      icon: <FaTrash />,
      onClick: (item) => handleDeleteAbout(item._id),
    },
  ];

  return (
    <div className="">
      <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">About Us Management</h2>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold dark:text-white">About Us</h3>
        <button
          onClick={() => setIsModalOpen(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          Add About Us
        </button>
      </div>
      {aboutsLoading ? (
        <div className="card text-center text-gray-600 dark:text-gray-300">Loading...</div>
      ) : !abouts || abouts.length === 0 ? (
        <div className="card text-center text-gray-600 dark:text-gray-300">No about us found.</div>
      ) : (
        <div className="card">
          <Table columns={aboutColumns} data={abouts || []} actions={aboutActions} />
        </div>
      )}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setAboutForm({
            title: '',
            content: '',
          });
          setEditingAboutId(null);
          setAboutErrors({});
        }}
        title={editingAboutId ? 'Edit About Us' : 'Add About Us'}
        className="max-w-full lg:max-w-4xl mx-4"
      >
        <div className="max-h-[80vh] overflow-y-auto p-4">
          <form onSubmit={handleAboutSubmit}>
            <div className="grid grid-cols-1 gap-4">
              <FormInput
                label="Title"
                name="title"
                value={aboutForm.title}
                onChange={handleAboutInputChange}
                required
                placeholder="Enter about us title"
                error={aboutErrors.title}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Content</label>
                <div className="mt-1 ckeditor-container">
                  <CKEditor
                    editor={ClassicEditor}
                    data={aboutForm.content}
                    onChange={handleEditorChange}
                    config={{
                      toolbar: [
                        'heading',
                        '|',
                        'bold',
                        'italic',
                        'underline',
                        'link',
                        '|',
                        'bulletedList',
                        'numberedList',
                        '|',
                        'blockQuote',
                        '|',
                        'undo',
                        'redo',
                      ],
                      language: 'en',
                    }}
                  />
                </div>
                {aboutErrors.content && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{aboutErrors.content}</p>
                )}
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <button
                type="button"
                onClick={() => {
                  setIsModalOpen(false);
                  setAboutForm({
                    title: '',
                    content: '',
                  });
                  setAboutErrors({});
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                disabled={aboutsLoading}
              >
                {editingAboutId ? 'Update About Us' : 'Add About Us'}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
}

export default AboutUs;